/**
 * AI-import candidate star-rating orchestration.
 *
 * When the user opts into star-rating analysis (the upload-step toggle), the
 * dialog rates each extracted plan property as a *candidate* — an unpersisted
 * preview computed from the extraction payload + a geocode (see the star-engine
 * candidate branch). The previews are held in dialog state, keyed to the plan
 * property, and surfaced on the Review/Decision steps.
 *
 * Nothing here persists a rating: the candidate branch writes nothing, and
 * persisting a client-supplied rating would be a forgeable engine bypass. On
 * Save the trusted by-id path recomputes each saved property (`recomputeSavedRatings`).
 */

import { useEffect, useRef, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { rateCandidate, rateProperty } from '@/services/star-rating.service';
import type { CandidateRating, CandidateRatingPayload } from '@/services/star-rating.service';
import { starRatingKeys } from '@/hooks/useStarRating';
import { propertyKeys } from '@/hooks/useProperties';
import type { AIImportPlanProperty } from '@/types/ai-import';

export type CandidateRatingStatus = 'loading' | 'done' | 'error';

/** One plan property's held preview rating (or its in-flight / failed state). */
export interface CandidateRatingEntry {
  status: CandidateRatingStatus;
  rating?: CandidateRating;
}

/** Preview ratings keyed by `planPropertyStarKey`. */
export type CandidateRatingMap = Record<string, CandidateRatingEntry>;

const str = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : v == null ? '' : String(v);

function toNum(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Position key for a plan property's held rating. The hook rates the stable
 * extraction source (`job.summary.properties`); the Review/Decision steps render
 * position-aligned COPIES with different object references, so identity keying is
 * impossible and content keying collides (two properties with identical address
 * parts would share one slot). Keying by array index is unique per position and
 * aligns the source with its copies, and survives in-place field edits because
 * the source is rated once. Limitation: reordering/removing properties during
 * review would misalign the index — uncommon in this flow.
 */
export function planPropertyStarKey(index: number): string {
  return 'pp' + index;
}

/** Geocodable single-line address assembled from the plan property's fields. */
export function assembleAddress(p: AIImportPlanProperty): string {
  const f = p.fields_to_apply ?? {};
  return [f.address_street, f.address_city, f.address_state, f.address_zip]
    .map(str)
    .filter(Boolean)
    .join(', ');
}

function buildCandidatePayload(p: AIImportPlanProperty): CandidateRatingPayload {
  const f = p.fields_to_apply ?? {};
  return {
    extracted_data: f,
    address: assembleAddress(p),
    name: typeof f.name === 'string' ? f.name : undefined,
    latitude: toNum(f.latitude),
    longitude: toNum(f.longitude),
  };
}

/** A property is rateable only if we can geocode it (an address) or already have coords. */
function isRateable(p: AIImportPlanProperty): boolean {
  const f = p.fields_to_apply ?? {};
  const hasCoords = toNum(f.latitude) != null && toNum(f.longitude) != null;
  return assembleAddress(p).length > 0 || hasCoords;
}

const CANDIDATE_CONCURRENCY = 3;

/**
 * Candidate-rate each plan property (bounded-parallel) once the plan is final,
 * holding the previews keyed by `planPropertyStarKey`. Dedupes by key (a poll /
 * re-render never re-rates), resets when the job changes, and degrades
 * gracefully — a failed rate leaves that property `error` (rendered as unrated).
 */
export function useAiImportCandidateRatings(opts: {
  jobId: string | null | undefined;
  properties: AIImportPlanProperty[] | undefined;
  enabled: boolean;
}): CandidateRatingMap {
  const { jobId, properties, enabled } = opts;
  const [ratings, setRatings] = useState<CandidateRatingMap>({});
  const startedRef = useRef<Set<string>>(new Set());
  const jobRef = useRef<string | null | undefined>(jobId);
  const mountedRef = useRef(true);
  // Bumped on job change / unmount so stale (unstarted or in-flight) workers
  // abandon their queue and never write a previous job's result into the map.
  const genRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Forget previous-job previews when the job changes.
  useEffect(() => {
    if (jobRef.current !== jobId) {
      jobRef.current = jobId;
      genRef.current += 1;
      startedRef.current = new Set();
      setRatings({});
    }
  }, [jobId]);

  useEffect(() => {
    if (!enabled || !properties || properties.length === 0) return;

    const gen = genRef.current;

    // Key each property by its INDEX into `properties` (aligned across the stable
    // source + its position-aligned copies on the Review/Decision steps).
    // Limitation: reordering/removing properties during review would misalign the
    // index — uncommon in this flow.
    const pending = properties
      .map((p, i) => ({ p, key: planPropertyStarKey(i) }))
      .filter(({ p, key }) => isRateable(p) && !startedRef.current.has(key));
    if (pending.length === 0) return;

    const loadingPatch: CandidateRatingMap = {};
    for (const { key } of pending) {
      startedRef.current.add(key);
      loadingPatch[key] = { status: 'loading' };
    }
    setRatings((prev) => ({ ...prev, ...loadingPatch }));

    let idx = 0;
    const worker = async () => {
      while (idx < pending.length) {
        // Abandon the queue if the job changed or the dialog closed.
        if (genRef.current !== gen || !mountedRef.current) return;
        const { p, key } = pending[idx++];
        try {
          const rating = await rateCandidate(buildCandidatePayload(p));
          if (genRef.current === gen && mountedRef.current) {
            setRatings((prev) => ({ ...prev, [key]: { status: 'done', rating } }));
          }
        } catch {
          if (genRef.current === gen && mountedRef.current) {
            setRatings((prev) => ({ ...prev, [key]: { status: 'error' } }));
          }
        }
      }
    };
    void Promise.all(
      Array.from({ length: Math.min(CANDIDATE_CONCURRENCY, pending.length) }, worker),
    );
  }, [enabled, properties]);

  return ratings;
}

const RECOMPUTE_CONCURRENCY = 2;

/**
 * On Save, recompute + persist the real rating for each saved property through
 * the trusted by-id path (`rateProperty`) — never persisting the client-side
 * preview. Fire-and-forget + bounded-parallel; a failure leaves that property
 * unrated. Query invalidation runs on the app-level `QueryClient`, so it holds
 * even though the Decision step unmounts to the Confirm screen.
 */
export function recomputeSavedRatings(ids: string[], queryClient: QueryClient): void {
  const uniq = Array.from(new Set(ids.filter(Boolean)));
  if (uniq.length === 0) return;

  let idx = 0;
  const run = async () => {
    while (idx < uniq.length) {
      const id = uniq[idx++];
      try {
        await rateProperty(id, false);
        queryClient.invalidateQueries({ queryKey: starRatingKeys.byProperty(id) });
        queryClient.invalidateQueries({ queryKey: propertyKeys.detail(id) });
      } catch {
        /* graceful: leave that property unrated */
      }
    }
  };
  void Promise.all(
    Array.from({ length: Math.min(RECOMPUTE_CONCURRENCY, uniq.length) }, run),
  ).then(() => {
    queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
  });
}
